// Product data with Curtain ↔ Fabric Sample relationships
// All fabrics available in both Wave and Pleated styles

export interface ColorVariant {
  id: string;
  name: string;
  hex: string;
  image?: string;
  // Array of images for product gallery (5 images per color)
  images?: string[];
}

export interface FabricSwatch {
  id: string;
  name: string;
  colors: ColorVariant[];
  material: string;
  price: number;
  description: string;
  relatedCurtainIds: string[];
}

export interface Curtain {
  id: string;
  name: string;
  category: 'wave' | 'pleated' | 'blackout' | 'linen' | 'sheer' | 'roller';
  fabricSwatchId: string;
  basePrice: number;
  images: string[];
  image?: string;
  description: string;
  features: string[];
  functions: ('blackout' | 'privacy' | 'light-filtering' | 'thermal')[];
  colors: ColorVariant[];
}

export interface CollectionInfo {
  id: string;
  name: string;
  slug: string;
  description: string;
  longDescription: string;
  faqs: { question: string; answer: string }[];
}

// Color definitions for reuse
const colorPalette = {
  naturalWhite: { id: 'natural-white', name: 'Naturvit', hex: '#F5F2E8' },
  stoneGrey: { id: 'stone-grey', name: 'Stengrå', hex: '#9A9A9A' },
  forestGreen: { id: 'forest-green', name: 'Skogsgrön', hex: '#2D4A3E' },
  sand: { id: 'sand', name: 'Sand', hex: '#D4C4A8' },
  charcoal: { id: 'charcoal', name: 'Antracit', hex: '#36454F' },
  white: { id: 'white', name: 'Vit', hex: '#FFFFFF' },
  oatmeal: { id: 'oatmeal', name: 'Havre', hex: '#E8DFD0' },
  dustyRose: { id: 'dusty-rose', name: 'Dusty Rose', hex: '#C9A9A6' },
  navy: { id: 'navy', name: 'Marinblå', hex: '#1B2838' },
  terracotta: { id: 'terracotta', name: 'Terrakotta', hex: '#C4755C' },
};

export const fabricSwatches: FabricSwatch[] = [
  {
    id: 'swatch-natural-linen',
    name: 'Europeiskt Linne',
    colors: [
      colorPalette.naturalWhite,
      colorPalette.oatmeal,
      colorPalette.sand,
      colorPalette.stoneGrey,
      colorPalette.dustyRose,
    ],
    material: '100% Linne',
    price: 0,
    description: 'Klassiskt europeiskt linne med vacker textur och naturligt fall. Ljusdämpande med elegant transparens.',
    relatedCurtainIds: ['curtain-wave-linen', 'curtain-pleated-linen']
  },
  {
    id: 'swatch-premium-velvet',
    name: 'Premium Sammet',
    colors: [
      colorPalette.forestGreen,
      colorPalette.navy,
      colorPalette.dustyRose,
      colorPalette.charcoal,
      colorPalette.terracotta,
    ],
    material: 'Bomullssammet',
    price: 0,
    description: 'Lyxig bomullssammet i djupa färger. Ger rummet värme och exklusiv känsla.',
    relatedCurtainIds: ['curtain-wave-velvet', 'curtain-pleated-velvet']
  },
  {
    id: 'swatch-cotton-blend',
    name: 'Bomullsblandning',
    colors: [
      colorPalette.white,
      colorPalette.sand,
      colorPalette.oatmeal,
      colorPalette.stoneGrey,
    ],
    material: '80% Bomull, 20% Polyester',
    price: 0,
    description: 'Mjuk bomullsblandning som är lättskött och tålig. Tidlös och praktisk.',
    relatedCurtainIds: ['curtain-wave-cotton', 'curtain-pleated-cotton']
  },
  {
    id: 'swatch-blackout',
    name: 'Mörkläggningsväv',
    colors: [
      colorPalette.charcoal,
      colorPalette.white,
      colorPalette.stoneGrey,
      colorPalette.navy,
      colorPalette.forestGreen,
    ],
    material: 'Mörkläggningsväv',
    price: 0,
    description: 'Högkvalitativ mörkläggningsväv som blockerar 99% av ljuset. Perfekt för sovrum.',
    relatedCurtainIds: ['curtain-wave-blackout', 'curtain-pleated-blackout']
  },
  {
    id: 'swatch-sheer-voile',
    name: 'Voile',
    colors: [
      colorPalette.white,
      colorPalette.naturalWhite,
      colorPalette.oatmeal,
    ],
    material: 'Polyester Voile',
    price: 0,
    description: 'Lätt och luftig voile som ger insynsskydd utan att stänga ute ljuset.',
    relatedCurtainIds: ['curtain-wave-sheer', 'curtain-pleated-sheer']
  }
];

export const curtains: Curtain[] = [
  // Wave Curtains
  {
    id: 'curtain-wave-linen',
    name: 'Wave Europeiskt Linne',
    category: 'wave',
    fabricSwatchId: 'swatch-natural-linen',
    basePrice: 1290,
    images: ['/placeholder.svg'],
    description: 'Elegant wavegardiner i naturligt europeiskt linne. Mjukt fall med modern S-vågformning som ger rummet en sofistikerad känsla.',
    features: ['S-vågformning', 'Takskena ingår', 'Naturligt linne', 'Måttbeställd'],
    functions: ['light-filtering', 'privacy'],
    colors: [
      colorPalette.naturalWhite,
      colorPalette.oatmeal,
      colorPalette.sand,
      colorPalette.stoneGrey,
      colorPalette.dustyRose,
    ]
  },
  {
    id: 'curtain-wave-velvet',
    name: 'Wave Premium Sammet',
    category: 'wave',
    fabricSwatchId: 'swatch-premium-velvet',
    basePrice: 1890,
    images: ['/placeholder.svg'],
    description: 'Lyxiga wavegardiner i mjuk bomullssammet. Ger rummet hotellkänsla med perfekt fall.',
    features: ['S-vågformning', 'Takskena ingår', 'Ljuddämpande', 'Måttbeställd'],
    functions: ['light-filtering', 'privacy', 'thermal'],
    colors: [
      // Forest Green with 5 example images
      { 
        ...colorPalette.forestGreen, 
        image: '/placeholder.svg', // Fabric swatch photo
        images: [
          '/placeholder.svg', // Image 1: Full curtain front
          '/placeholder.svg', // Image 2: Detail close-up
          '/placeholder.svg', // Image 3: Room setting
          '/placeholder.svg', // Image 4: Fabric texture
          '/placeholder.svg', // Image 5: Styling shot
        ]
      },
      { 
        ...colorPalette.navy, 
        image: '/placeholder.svg',
        images: [
          '/placeholder.svg',
          '/placeholder.svg',
          '/placeholder.svg',
          '/placeholder.svg',
          '/placeholder.svg',
        ]
      },
      { 
        ...colorPalette.dustyRose, 
        image: '/placeholder.svg',
        images: [
          '/placeholder.svg',
          '/placeholder.svg',
          '/placeholder.svg',
          '/placeholder.svg',
          '/placeholder.svg',
        ]
      },
      { 
        ...colorPalette.charcoal, 
        image: '/placeholder.svg',
        images: [
          '/placeholder.svg',
          '/placeholder.svg',
          '/placeholder.svg',
          '/placeholder.svg',
          '/placeholder.svg',
        ]
      },
      { 
        ...colorPalette.terracotta, 
        image: '/placeholder.svg',
        images: [
          '/placeholder.svg',
          '/placeholder.svg',
          '/placeholder.svg',
          '/placeholder.svg',
          '/placeholder.svg',
        ]
      },
    ]
  },
  {
    id: 'curtain-wave-cotton',
    name: 'Wave Bomullsblandning',
    category: 'wave',
    fabricSwatchId: 'swatch-cotton-blend',
    basePrice: 990,
    images: ['/placeholder.svg'],
    description: 'Klassiska wavegardiner i mjuk bomullsblandning. Tidlös elegans för alla rum.',
    features: ['S-vågformning', 'Takskena ingår', 'Lättskött', 'Måttbeställd'],
    functions: ['light-filtering'],
    colors: [
      colorPalette.white,
      colorPalette.sand,
      colorPalette.oatmeal,
      colorPalette.stoneGrey,
    ]
  },
  {
    id: 'curtain-wave-blackout',
    name: 'Wave Mörkläggning',
    category: 'blackout',
    fabricSwatchId: 'swatch-blackout',
    basePrice: 1590,
    images: ['/placeholder.svg'],
    description: 'Effektiva mörkläggningsgardiner med elegant wave-sömnad. Perfekt för sovrum och hemmabiorum.',
    features: ['99% mörkläggning', 'S-vågformning', 'Ljuddämpande', 'Måttbeställd'],
    functions: ['blackout', 'thermal', 'privacy'],
    colors: [
      colorPalette.charcoal,
      colorPalette.white,
      colorPalette.stoneGrey,
      colorPalette.navy,
      colorPalette.forestGreen,
    ]
  },
  {
    id: 'curtain-wave-sheer',
    name: 'Wave Voile Insynsskydd',
    category: 'sheer',
    fabricSwatchId: 'swatch-sheer-voile',
    basePrice: 690,
    images: ['/placeholder.svg'],
    description: 'Lätta voilegardiner med wave-sömnad som ger insynsskydd utan att stänga ute dagsljuset.',
    features: ['Insynsskydd', 'S-vågformning', 'Lätt material', 'Måttbeställd'],
    functions: ['privacy', 'light-filtering'],
    colors: [
      colorPalette.white,
      colorPalette.naturalWhite,
      colorPalette.oatmeal,
    ]
  },
  // Pleated Curtains (Veckband)
  {
    id: 'curtain-pleated-linen',
    name: 'Veckband Europeiskt Linne',
    category: 'pleated',
    fabricSwatchId: 'swatch-natural-linen',
    basePrice: 1190,
    images: ['/placeholder.svg'],
    description: 'Traditionella veckbandsgardiner i vackert europeiskt linne. Klassisk elegans med perfekta veck.',
    features: ['Dubbla veck', 'Sydda veckband', 'Naturligt linne', 'Måttbeställd'],
    functions: ['light-filtering', 'privacy'],
    colors: [
      colorPalette.naturalWhite,
      colorPalette.oatmeal,
      colorPalette.sand,
      colorPalette.stoneGrey,
      colorPalette.dustyRose,
    ]
  },
  {
    id: 'curtain-pleated-velvet',
    name: 'Veckband Premium Sammet',
    category: 'pleated',
    fabricSwatchId: 'swatch-premium-velvet',
    basePrice: 1790,
    images: ['/placeholder.svg'],
    description: 'Klassiska veckbandsgardiner i lyxig bomullssammet. Traditionell elegans med modern känsla.',
    features: ['Dubbla veck', 'Sydda veckband', 'Ljuddämpande', 'Måttbeställd'],
    functions: ['light-filtering', 'privacy', 'thermal'],
    colors: [
      colorPalette.forestGreen,
      colorPalette.navy,
      colorPalette.dustyRose,
      colorPalette.charcoal,
      colorPalette.terracotta,
    ]
  },
  {
    id: 'curtain-pleated-cotton',
    name: 'Veckband Bomullsblandning',
    category: 'pleated',
    fabricSwatchId: 'swatch-cotton-blend',
    basePrice: 890,
    images: ['/placeholder.svg'],
    description: 'Klassiska veckbandsgardiner i mjuk bomullsblandning. Tidlös och lättskött.',
    features: ['Dubbla veck', 'Sydda veckband', 'Lättskött', 'Måttbeställd'],
    functions: ['light-filtering'],
    colors: [
      colorPalette.white,
      colorPalette.sand,
      colorPalette.oatmeal,
      colorPalette.stoneGrey,
    ]
  },
  {
    id: 'curtain-pleated-blackout',
    name: 'Veckband Mörkläggning',
    category: 'blackout',
    fabricSwatchId: 'swatch-blackout',
    basePrice: 1490,
    images: ['/placeholder.svg'],
    description: 'Mörkläggningsgardiner med klassisk veckbandssömnad. Perfekt ljusblockering med traditionell stil.',
    features: ['99% mörkläggning', 'Dubbla veck', 'Fodrade', 'Måttbeställd'],
    functions: ['blackout', 'privacy', 'thermal'],
    colors: [
      colorPalette.charcoal,
      colorPalette.white,
      colorPalette.stoneGrey,
      colorPalette.navy,
      colorPalette.forestGreen,
    ]
  },
  {
    id: 'curtain-pleated-sheer',
    name: 'Veckband Voile Insynsskydd',
    category: 'sheer',
    fabricSwatchId: 'swatch-sheer-voile',
    basePrice: 590,
    images: ['/placeholder.svg'],
    description: 'Lätta voilegardiner med klassiska veckband. Elegant insynsskydd utan att stänga ute ljuset.',
    features: ['Insynsskydd', 'Dubbla veck', 'Lätt material', 'Måttbeställd'],
    functions: ['privacy', 'light-filtering'],
    colors: [
      colorPalette.white,
      colorPalette.naturalWhite,
      colorPalette.oatmeal,
    ]
  }
];

// Category buttons for navigation
// collectionHandle is the Shopify collection handle to fetch products from
// If collectionHandle exists, products are fetched from that collection (preserves Shopify order)
// If productQuery is set, it's used as fallback for filtering
export const categoryButtons = [
  { id: 'alla-gardiner', name: 'Alla gardiner', collectionHandle: 'alla-gardiner' },
  { id: 'tunna-linnegardiner', name: 'Tunna linnegardiner', collectionHandle: 'linnegardiner' },
  { id: 'grova-linnegardiner', name: 'Grova linnegardiner', collectionHandle: 'linnegardiner' },
  { id: 'hissgardiner', name: 'Hissgardiner', collectionHandle: 'hissgardiner' },
  { id: 'rullgardiner', name: 'Rullgardiner', collectionHandle: 'rullgardiner' },
  { id: 'linnegardiner', name: 'Linnegardiner', collectionHandle: 'linnegardiner' },
  { id: 'skenor-tillbehor', name: 'Skenor och tillbehör', collectionHandle: 'skenor-och-tillbehor' },
  { id: 'alla-produkter', name: 'Alla produkter', collectionHandle: 'alla-produkter' },
];

// Product groups for display order on "Alla produkter" page
export const productGroups = [
  { id: 'gardiner', name: 'Gardiner', keywords: ['gardin', 'curtain', 'wave', 'veck'] },
  { id: 'hissgardiner', name: 'Hissgardiner', keywords: ['hissgardin', 'roman', 'raffgardin'] },
  { id: 'rullgardiner', name: 'Rullgardiner', keywords: ['rullgardin', 'roller'] },
  { id: 'skenor-tillbehor', name: 'Skenor och tillbehör', keywords: ['skena', 'skenor', 'tillbehor', 'krok', 'ring', 'tyngd'] },
];

// Legacy categories export for backwards compatibility
export const categories = [
  { id: 'alla-gardiner', name: 'Alla gardiner', description: 'Hela gardinsortimentet' },
  { id: 'gardinlangder', name: 'Gardinlängder', description: 'Måttbeställda gardiner' },
  { id: 'hissgardiner', name: 'Hissgardiner', description: 'Praktiska hissgardiner' },
  { id: 'rullgardiner', name: 'Rullgardiner', description: 'Praktisk minimalism' },
  { id: 'skenor-tillbehor', name: 'Skenor och tillbehör', description: 'Gardinskenor, krokar & mer' },
  { id: 'wave', name: 'Wavegardiner', description: 'Modern S-vågformning' },
  { id: 'pleated', name: 'Veckband', description: 'Klassisk elegans' },
  { id: 'blackout', name: 'Mörkläggning', description: 'Blockerar ljus' },
  { id: 'linen', name: 'Linne', description: 'Naturliga material' },
  { id: 'sheer', name: 'Insynsskydd', description: 'Lätta voilegardiner' },
];

// Collection info with descriptions and FAQs
export const collections: CollectionInfo[] = [
  {
    id: 'all',
    name: 'Alla gardiner',
    slug: 'all',
    description: 'Upptäck hela vårt sortiment av måttbeställda gardiner',
    longDescription: `Välkommen till Gardinovas kompletta gardinkollektion. Här hittar du alla våra måttbeställda gardiner – från moderna wavegardiner till klassiska veckbandsgardiner, från lätta voilegardiner till effektiva mörkläggningsgardiner.

Varje gardin sys med omsorg i vår svenska ateljé och skräddarsys efter dina exakta mått. Vi använder bara högkvalitativa tyger som håller i många år, och alla våra wavegardiner levereras med takskena inkluderad. Oavsett om du söker en minimalistisk look eller klassisk elegans, en lätt slöja eller total mörkläggning – hos oss hittar du gardinen som passar just ditt hem.`,
    faqs: [
      { question: 'Hur fungerar måttbeställning?', answer: 'Du mäter ditt fönster enligt våra guider, väljer tyg, färg och sömnad (wave eller veckband), och vi syr din gardin efter dina exakta mått. Leverans sker inom 2-3 veckor.' },
      { question: 'Vad är skillnaden mellan wave och veckband?', answer: 'Wavegardiner har en modern S-vågformning och levereras med takskena. Veckbandsgardiner har klassiska dubbla veck och passar på gardinstång eller skena med krokar.' },
      { question: 'Kan jag beställa tygprover?', answer: 'Absolut! Beställ upp till 5 gratis tygprover så du kan se och känna på materialen hemma innan du bestämmer dig.' },
      { question: 'Hur lång är leveranstiden?', answer: 'Måttbeställda gardiner levereras inom 2-3 veckor. Tygprover skickas inom 1-3 arbetsdagar.' },
      { question: 'Kan jag returnera måttbeställda gardiner?', answer: 'Eftersom varje gardin sys speciellt för dig kan vi tyvärr inte ta emot returer på måttbeställda produkter. Vi rekommenderar att beställa tygprover först.' },
      { question: 'Ingår skenan i priset för wavegardiner?', answer: 'Ja, takskenan ingår alltid i priset för wavegardiner. Du får allt du behöver för en komplett installation.' },
      { question: 'Erbjuder ni montering?', answer: 'Vi erbjuder monteringshjälp i utvalda områden. Kontakta oss för mer information om professionell installation.' },
      { question: 'Hur sköter jag mina gardiner?', answer: 'De flesta av våra tyger kan maskintvättas på 30-40°C. Se den specifika skötselinformationen för ditt valda tyg.' }
    ]
  },
  {
    id: 'wave',
    name: 'Wavegardiner',
    slug: 'wave',
    description: 'Moderna gardiner med elegant S-vågformning',
    longDescription: `Våra wavegardiner representerar det bästa av modern skandinavisk design. Med sin karakteristiska S-vågformning skapar de ett mjukt, flytande fall som förvandlar varje rum. Varje gardin sys i vår egen ateljé i Sverige med samma omsorg och precision som vi haft sedan starten.

Wave-systemet ger en ren, minimalistisk look utan synliga krokar eller upphängningar. Takskenan ingår alltid, vilket gör installationen enkel och resultatet professionellt. Välj mellan våra fem olika tygkollektioner och upp till fem färger per tyg för att hitta din perfekta gardin.`,
    faqs: [
      { question: 'Vad är en wavegardin?', answer: 'En wavegardin har en speciell sömnad som skapar en mjuk S-vågformning istället för traditionella veck. Detta ger ett modernt, minimalistiskt utseende med ett vackert fall.' },
      { question: 'Ingår skenan i priset?', answer: 'Ja, takskenan ingår alltid i priset för våra wavegardiner. Du får allt du behöver för en komplett installation.' },
      { question: 'Hur mäter jag för wavegardiner?', answer: 'Mät bredden på ditt fönster eller den yta du vill täcka, och lägg till 20-30 cm på varje sida för bästa resultat. Längden mäts från tak till golv eller önskad sluthöjd.' },
      { question: 'Kan jag få wavegardiner i alla tyger?', answer: 'Ja, samtliga våra tyger finns tillgängliga som wavegardiner. Från lätt voile till mörkläggningsväv.' },
      { question: 'Hur lång är leveranstiden?', answer: 'Våra måttbeställda gardiner levereras inom 2-3 veckor. Vi kontaktar dig om det skulle bli förseningar.' },
      { question: 'Kan jag returnera måttbeställda gardiner?', answer: 'Eftersom varje gardin sys speciellt för dig kan vi tyvärr inte ta emot returer på måttbeställda produkter. Vi rekommenderar att beställa gratis tygprover först.' },
      { question: 'Hur sköter jag mina wavegardiner?', answer: 'De flesta av våra tyger kan maskintvättas på 30°. Se alltid den specifika skötselinformationen för ditt valda tyg.' },
      { question: 'Erbjuder ni montering?', answer: 'Vi erbjuder monteringshjälp i vissa områden. Kontakta oss för mer information om professionell installation.' }
    ]
  },
  {
    id: 'pleated',
    name: 'Veckbandsgardiner',
    slug: 'pleated',
    description: 'Klassiska gardiner med traditionell elegans',
    longDescription: `Veckbandsgardiner är det tidlösa valet för den som uppskattar klassisk elegans. Med sina karaktäristiska dubbla veck skapar de ett fylligt, lyxigt intryck som passar både moderna och traditionella hem.

Varje gardin sys med omsorg i vår svenska ateljé med sydda veckband av högsta kvalitet. Veckbanden är designade för att hänga perfekt på gardinstång eller skena. Välj mellan våra fem tygkollektioner i upp till fem färger per tyg.`,
    faqs: [
      { question: 'Vad är veckbandsgardiner?', answer: 'Veckbandsgardiner har sydda band i toppen som skapar jämna, traditionella veck. De ger ett fylligt, klassiskt utseende.' },
      { question: 'Passar veckband till gardinstång?', answer: 'Ja, veckbandsgardiner fungerar utmärkt med gardinstång och ringar. De kan även hängas på skena med krokar.' },
      { question: 'Hur bred ska gardinen vara?', answer: 'För ett fylligt resultat rekommenderar vi att gardinbredden är 2-2,5 gånger fönsterbredden.' },
      { question: 'Finns det i samma tyger som wave?', answer: 'Ja, exakt samma tyger och färger finns tillgängliga i både wave- och veckbandssömnad.' },
      { question: 'Vad kostar veckbandsgardiner?', answer: 'Priset beror på tyg, bredd och längd. Se "Från"-priset på varje produkt för utgångspriset.' },
      { question: 'Hur snabbt levererar ni?', answer: 'Leveranstiden är 2-3 veckor för måttbeställda gardiner.' },
      { question: 'Kan jag beställa tygprover?', answer: 'Absolut! Beställ upp till 5 gratis tygprover så du kan känna på materialet hemma.' },
      { question: 'Hur mäter jag rätt längd?', answer: 'Mät från upphängningspunkten (stång eller skena) till golvet eller önskad sluthöjd. Vi rekommenderar 1-2 cm ovanför golvet.' }
    ]
  },
  {
    id: 'blackout',
    name: 'Mörkläggningsgardiner',
    slug: 'blackout',
    description: 'Blockerar 99% av ljuset för bättre sömn',
    longDescription: `Våra mörkläggningsgardiner är den perfekta lösningen för sovrum, hemmabiorum och alla rum där du vill ha full kontroll över ljuset. Med högkvalitativ mörkläggningsväv som blockerar upp till 99% av allt ljus får du den totala mörkret du behöver.

Utöver ljusblockering ger våra mörkläggningsgardiner också utmärkt ljuddämpning och termisk isolering. De finns i både wave- och veckbandssömnad, så du kan välja den stil som passar ditt hem bäst.`,
    faqs: [
      { question: 'Hur mycket ljus blockerar mörkläggningsgardiner?', answer: 'Våra mörkläggningsgardiner blockerar upp till 99% av allt inkommande ljus.' },
      { question: 'Passar de i sovrum?', answer: 'Absolut, de är perfekta för sovrum där du vill sova ostört, särskilt under ljusa sommarkvällar.' },
      { question: 'Isolerar de mot värme och kyla?', answer: 'Ja, mörkläggningsväven har utmärkta termiska egenskaper som hjälper till att hålla rummet svalt på sommaren och varmt på vintern.' },
      { question: 'Finns mörkläggning i wave-sömnad?', answer: 'Ja, du kan välja mellan wave- och veckbandssömnad för alla våra mörkläggningstyger.' },
      { question: 'Vilka färger finns?', answer: 'Mörkläggningsväven finns i antracit, vit, stengrå, marinblå och skogsgrön.' },
      { question: 'Är de tunga?', answer: 'Mörkläggningsväven är något tyngre än vanliga tyger, men våra skenor och upphängningar är designade för att hantera vikten utan problem.' },
      { question: 'Kan de användas i barnrum?', answer: 'Ja, de är utmärkta för barnrum och hjälper barn att sova bättre, särskilt under ljusa nätter.' },
      { question: 'Hur tvättar jag dem?', answer: 'Våra mörkläggningsgardiner kan försiktigt tvättas på 30°C. Tumla inte och undvik starka rengöringsmedel.' }
    ]
  },
  {
    id: 'sheer',
    name: 'Insynsskyddsgardiner',
    slug: 'sheer',
    description: 'Lätta gardiner som släpper in ljuset',
    longDescription: `Våra insynsskyddsgardiner i voile är det perfekta valet när du vill ha avskildhet utan att offra dagsljus. Den lätta, transparenta väven filtrerar ljuset vackert och skapar en mjuk, drömlik atmosfär.

Voilegardiner fungerar utmärkt som innergardiner i kombination med tjockare yttergardiner, eller som enskild lösning i rum där full mörkläggning inte behövs. De finns i både wave- och veckbandssömnad.`,
    faqs: [
      { question: 'Ger voilegardiner insynsskydd?', answer: 'Ja, voiletyget ger bra insynsskydd på dagtid samtidigt som det släpper in ljus. På kvällen, med lampor tända, minskar insynsskyddet.' },
      { question: 'Kan de användas ensamma?', answer: 'Absolut, voilegardiner är vackra som enskilda gardiner i kök, vardagsrum eller andra rum där du inte behöver full mörkläggning.' },
      { question: 'Passar de som innergardiner?', answer: 'Ja, de fungerar perfekt som innergardiner tillsammans med mörkare yttergardiner.' },
      { question: 'Vilka färger finns?', answer: 'Voiletyget finns i vit, naturvit och havre – neutrala toner som passar i alla miljöer.' },
      { question: 'Är de svåra att sköta?', answer: 'Nej, våra voilegardiner är lättskötta och kan maskintvättas på 30°C.' },
      { question: 'Finns de i wave-sömnad?', answer: 'Ja, voilegardiner finns i både wave- och veckbandssömnad.' },
      { question: 'Hur mäter jag för voilegardiner?', answer: 'Samma principer gäller som för andra gardiner. Vi rekommenderar 2-2,5 gånger fönsterbredden för fylligt fall.' },
      { question: 'Bleks de i solen?', answer: 'Våra tyger är ljusfasta, men som med alla textilier rekommenderar vi att undvika direkt, stark sol under längre perioder.' }
    ]
  },
  {
    id: 'linen',
    name: 'Linnegardiner',
    slug: 'linen',
    description: 'Naturliga material med skandinavisk känsla',
    longDescription: `Vårt europeiska linne representerar det allra finaste inom naturliga textilier. Varje gardin sys i 100% linne som odlas och vävs i Europa, med den karakteristiska textur och det vackra fall som bara äkta linne kan ge.

Linnegardiner åldras vackert och blir bara mjukare och skönare med tiden. De är perfekta för den skandinaviska inredningsstilen och ger rummet en naturlig, avslappnad elegans. Finns i både wave- och veckbandssömnad.`,
    faqs: [
      { question: 'Är det 100% linne?', answer: 'Ja, våra linnegardiner är tillverkade av 100% europeiskt linne av högsta kvalitet.' },
      { question: 'Skrynklar linne mycket?', answer: 'Linne har en naturlig tendens att skrynkla, men många uppskattar denna karaktär. Gardiner som hänger rätar ut sig naturligt.' },
      { question: 'Hur sköter jag linnegardiner?', answer: 'Linnegardiner kan maskintvättas på 40°C. Häng dem våta för att minimera skrynkling. Stryk på hög värme om önskat.' },
      { question: 'Krymper linne i tvätt?', answer: 'Våra linnegardiner är förkrympta för att minimera krympning. Följ alltid skötselråden.' },
      { question: 'Vilka färger finns?', answer: 'Linnetyget finns i naturvit, havre, sand, stengrå och dusty rose.' },
      { question: 'Är linne hållbart?', answer: 'Ja, linne är ett av de mest hållbara naturliga materialen. Det är biologiskt nedbrytbart och kräver mindre vatten och bekämpningsmedel att odla.' },
      { question: 'Passar linne i alla rum?', answer: 'Linnegardiner passar utmärkt i vardagsrum, sovrum och matrum. De ger en naturlig, lugn atmosfär.' },
      { question: 'Kan jag få linne med mörkläggning?', answer: 'Våra rena linnegardiner är ljusdämpande men inte mörkläggande. För mörkläggning, se våra mörkläggningsgardiner.' }
    ]
  }
];

// Helper functions to get related products
export function getFabricSwatchById(id: string): FabricSwatch | undefined {
  return fabricSwatches.find(swatch => swatch.id === id);
}

export function getCurtainById(id: string): Curtain | undefined {
  return curtains.find(curtain => curtain.id === id);
}

export function getRelatedCurtains(fabricSwatchId: string): Curtain[] {
  const swatch = getFabricSwatchById(fabricSwatchId);
  if (!swatch) return [];
  return swatch.relatedCurtainIds.map(id => getCurtainById(id)).filter(Boolean) as Curtain[];
}

export function getFabricSwatchForCurtain(curtainId: string): FabricSwatch | undefined {
  const curtain = getCurtainById(curtainId);
  if (!curtain) return undefined;
  return getFabricSwatchById(curtain.fabricSwatchId);
}

export function getCurtainsByCategory(category: string): Curtain[] {
  return curtains.filter(curtain => curtain.category === category);
}

export function getCollectionBySlug(slug: string): CollectionInfo | undefined {
  return collections.find(c => c.slug === slug);
}

export function formatPrice(price: number): string {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: 0
  }).format(price);
}

// Accessories / Complementary products (will be replaced by Shopify Search & Discovery)
export interface Accessory {
  id: string;
  name: string;
  subtitle: string;
  price: number;
  image: string;
}

export const accessories: Accessory[] = [
  {
    id: 'accessory-hooks-low',
    name: 'Gardinkrokar 10st',
    subtitle: 'Låg (för gardinskena)',
    price: 50,
    image: '/placeholder.svg'
  },
  {
    id: 'accessory-hooks-high',
    name: 'Gardinkrokar 10st',
    subtitle: 'Hög (för gardinstång)',
    price: 50,
    image: '/placeholder.svg'
  },
  {
    id: 'accessory-weights',
    name: 'Gardinvikt 2-pack',
    subtitle: 'För bättre fall',
    price: 50,
    image: '/placeholder.svg'
  }
];

export function getAccessories(): Accessory[] {
  return accessories;
}
